import * as ko from 'knockout';
import {ObjectTypeId} from '../../../constants';
import {getHash} from '../../../constants/RouteDefinitions';
import {TSyncfield_User} from '../../../constants/Syncfields';
import {PLEASE_WAIT_MESSAGE} from '../../../constants/Language';
import {IObjectData, IOverride} from '../../../index';
import {getObjectDefinition} from '../../../util/misc';
import {nsSyncFields} from '../../../req/nsSyncFields';
import {nsOverride} from '../../override/nsOverride';
import {GROUP_MEMBERS_OVERRIDE_DETAILS_KEYS} from '../../override/overrideDetailsKeys';
import {nsGroupMemberOverride} from './nsGroupMemberOverride';
import canMutateMembershipOverride = nsGroupMemberOverride.canMutateMembershipOverride;
import canShowMembershipOverrideAction = nsGroupMemberOverride.canShowMembershipOverrideAction;
import getMembershipOverrideAction = nsGroupMemberOverride.getMembershipOverrideAction;
import isMembershipOverrideDeleted = nsGroupMemberOverride.isMembershipOverrideDeleted;
import isOverrideEffectivelyRemoved = nsOverride.isOverrideEffectivelyRemoved;
import TObjectData_basic = nsSyncFields.TObjectData_basic;

/** Row VM dependencies on {@link GroupMemberSearch} (avoids circular import). */
export interface IGroupMemberSearchParent {
    onMemberRowActionClick(user: IObjectData): void;
    getOverrideForUser(user: IObjectData): IOverride | null;
    memberActionsEnabled: KnockoutComputed<boolean>;
    getSyncfieldValue(key: TSyncfield_User, data: IObjectData): string;
    getDepartmentDisplayText(user: IObjectData): string;
    getManagerDisplayText(user: IObjectData): string;
    getDepartmentForUser(user: IObjectData): TObjectData_basic | undefined;
    getManagerForUser(user: IObjectData): TObjectData_basic | undefined;
    updateOverrideComment(overrideId: number, comment: string): Promise<void>;
}

/** Expandable grid row for one group member. */
export class GroupMemberRowVm {
    readonly isExpanded = ko.observable(false);
    readonly isEditingOverrideComment = ko.observable(false);
    readonly isSavingOverrideDetails = ko.observable(false);
    readonly overrideCommentDraft = ko.observable('');
    readonly overridePanelRevision = ko.observable(0);
    readonly overrideDetailsWaitMessage = PLEASE_WAIT_MESSAGE;
    readonly overrideDetailsKeys = GROUP_MEMBERS_OVERRIDE_DETAILS_KEYS;

    constructor(
        user: IObjectData,
        readonly parentSearch: IGroupMemberSearchParent,
    ) {
        this.user = user;
    }

    user: IObjectData;

    /** Updates row data after a search refresh while preserving expand/edit UI state. */
    setUser(user: IObjectData): void {
        this.user = user;
        this.bumpOverridePanelRevision();
    }

    getRowBindings() {
        return {
            attr: {'aria-expanded': this.isExpanded},
        };
    }

    /** Rows without a membership override are not expandable. */
    hasOverride(): boolean {
        return !!this.getOverride();
    }

    doExpandToggle = (): void => {
        if (this.hasOverride()) {
            this.isExpanded(!this.isExpanded());
        }
    };

    getOverride() {
        return this.parentSearch.getOverrideForUser(this.user);
    }

    /** Plain row action kind from override state (or remove when no override). */
    rowActionKind(): nsGroupMemberOverride.MembershipMemberActionKind {
        const override = this.getOverride();
        return override ? getMembershipOverrideAction(override).actionKind : 'remove';
    }

    canRunRowAction(): boolean {
        return this.parentSearch.memberActionsEnabled();
    }

    onRowActionClick = (): void => {
        this.parentSearch.onMemberRowActionClick(this.user);
    };

    /** Display params for shared `component/override/override-status`. */
    getOverrideStatusParams() {
        const override = this.getOverride();
        if (!override) {
            return null;
        }
        const display = nsGroupMemberOverride.getMembershipOverrideDisplay(override);
        return {
            translationKey: display.translationKey,
            pillVariant: display.pillVariant,
            hasComment: !!override.comment?.trim(),
        };
    }

    /** Same CMS key as the status pill (pending/synced included). */
    getOverrideSectionHeadingKey(): string | null {
        return this.getOverrideStatusParams()?.translationKey ?? null;
    }

    getMarkedValue(key: TSyncfield_User) {
        return this.parentSearch.getSyncfieldValue(key, this.user);
    }

    departmentDisplayText() {
        return this.parentSearch.getDepartmentDisplayText(this.user);
    }

    managerDisplayText() {
        return this.parentSearch.getManagerDisplayText(this.user);
    }

    userHref(): string | null {
        if (!this.user.id) {
            return null;
        }
        const routeId = getObjectDefinition(ObjectTypeId.User)?.routeDetails;
        if (!routeId) {
            return null;
        }
        return getHash(routeId, {id: this.user.id});
    }

    hasUserLink(): boolean {
        return !!this.userHref();
    }

    departmentHref(): string | null {
        const department = this.parentSearch.getDepartmentForUser(this.user);
        if (!department?.id) {
            return null;
        }
        const routeId = getObjectDefinition(ObjectTypeId.Department)?.routeDetails;
        if (!routeId) {
            return null;
        }
        return getHash(routeId, {id: department.id});
    }

    hasDepartmentLink(): boolean {
        return !!this.departmentHref() && !!this.departmentDisplayText();
    }

    managerHref(): string | null {
        const manager = this.parentSearch.getManagerForUser(this.user);
        if (!manager?.id) {
            return null;
        }
        const routeId = getObjectDefinition(ObjectTypeId.User)?.routeDetails;
        if (!routeId) {
            return null;
        }
        return getHash(routeId, {id: manager.id});
    }

    hasManagerLink(): boolean {
        return !!this.managerHref() && !!this.managerDisplayText();
    }

    overrideIdText(): string {
        const id = this.getOverride()?.id;
        return id == null ? '' : String(id);
    }

    overrideValueText(): string {
        return this.getOverride()?.value ?? '';
    }

    overrideSyncImportFileIdText(): string {
        const id = this.getOverride()?.syncronizedimportfileid;
        return id ? String(id) : '';
    }

    overrideCommentText(): string {
        return this.getOverride()?.comment ?? '';
    }

    overrideOverriddenWhen(): string {
        return this.getOverride()?.overriddenwhen ?? '';
    }

    overrideOverriddenByName(): string {
        return this.getOverride()?.overriddenby ?? '';
    }

    overrideDeletedWhen(): string {
        return this.getOverride()?.deletedwhen ?? '';
    }

    overrideDeletedByName(): string {
        return this.getOverride()?.deletedby ?? '';
    }

    /** True when the membership override is soft-deleted (`deletedbyid` set). */
    isOverrideDeleted(): boolean {
        const override = this.getOverride();
        return !!override && isMembershipOverrideDeleted(override);
    }

    /** Active remove or soft-deleted add — expanded row uses the danger accent. */
    isEffectivelyRemoved(): boolean {
        return isOverrideEffectivelyRemoved(this.getOverride());
    }

    canEditOverrideComment(): boolean {
        const override = this.getOverride();
        return !!override && canMutateMembershipOverride(override);
    }

    startEditOverrideComment = (): void => {
        this.overrideCommentDraft(this.overrideCommentText());
        this.isEditingOverrideComment(true);
    };

    cancelEditOverrideComment = (): void => {
        this.isEditingOverrideComment(false);
        this.overrideCommentDraft(this.overrideCommentText());
    };

    submitOverrideComment = (): void => {
        const override = this.getOverride();
        if (!override?.id) {
            return;
        }
        this.isEditingOverrideComment(false);
        this.isSavingOverrideDetails(true);
        void this.parentSearch
            .updateOverrideComment(override.id, this.overrideCommentDraft())
            .finally(() => this.isSavingOverrideDetails(false));
    };

    /** Notify the expanded panel after this row's override data is replaced. */
    bumpOverridePanelRevision(): void {
        this.overridePanelRevision(this.overridePanelRevision() + 1);
    }

    /** Whether the row action button should render (hidden for terminal soft-deleted overrides). */
    showRowAction(): boolean {
        const override = this.getOverride();
        return !override || canShowMembershipOverrideAction(override);
    }
}
